import { ITheme, loadTheme } from "@fluentui/react";

import {
  defaultDarkTheme,
  defaultLightTheme,
  defaultDarkThemeNext,
  defaultLightThemeNext,
} from "./DefaultThemes";
import { CustomAppTheme } from "../../CustomAppTheme";

// Theme types supported for the users (the number of options here may grow in the future).
export enum AppTheme {
  Light,
  Dark,
}

// This class will manage the Themes offered to the users by the App.
// It allow developers to define themes that are stored in a dictionary, and
// then these can be used to load the Theme to the UI and update the UI controls.
export class AppThemeManager {
  private static instance: AppThemeManager;

  private AppThemeList: { [key in AppTheme]: CustomAppTheme } = {
    [AppTheme.Light]: defaultLightThemeNext,
    [AppTheme.Dark]: defaultDarkThemeNext,
  };

  private AppThemeListV8: { [key in AppTheme]: ITheme } = {
    [AppTheme.Light]: defaultLightTheme,
    [AppTheme.Dark]: defaultDarkTheme,
  };
  private currentTheme: AppTheme = AppTheme.Light;
  private themeChangedCallbacks: Array<() => void> = [];

  // Singleton
  public static getInstance(): AppThemeManager {
    if (!AppThemeManager.instance) {
      AppThemeManager.instance = new AppThemeManager();
    }
    return AppThemeManager.instance;
  }

  // Get current Theme mode (as an AppTheme).
  // For example: if you just want to know if we are in dark mode or not.
  public getThemeMode(): AppTheme {
    return this.currentTheme;
  }

  // Get the current Theme (as the full ITheme).
  // Future note: If we end up storing the v8 theme colors in vnext theme, we could
  // call convertTheme here to get the correct theme.
  public getTheme(): ITheme {
    return this.AppThemeListV8[this.currentTheme];
  }

  public getThemeNext(): CustomAppTheme {
    return this.AppThemeList[this.currentTheme];
  }

  // Set a new Theme definition that can be used by the App.
  // Note that this adds a theme to the dictionary, it does not load it to the UI.
  public setTheme(
    appTheme: AppTheme,
    v8Theme: ITheme,
    newTheme?: CustomAppTheme
  ) {
    // Save the new Theme definition
    if (newTheme) {
      this.AppThemeList[appTheme] = newTheme;
    }

    this.AppThemeListV8[appTheme] = v8Theme;
  }

  // Load a given Theme to the UI (update current theme and re-render all consumer controls).
  public loadTheme(appTheme: AppTheme) {
    this.currentTheme = appTheme;
    // Load the new theme to the UI (this will update the current global Fabric theme, as well as
    // trigger re-renders in all controls set as consumers of the Theme).
    // Future note: If we end up storing the v8 theme colors in vnext theme, we could
    // call convertTheme here to load the correct theme.
    loadTheme(this.AppThemeListV8[appTheme]);
    this.themeChangedCallbacks.forEach((callback) => callback());
  }

  // Returns requested theme, for when we need to access the palette
  // for a non-active theme (e.g. header initial load)
  // Future note: If we end up storing the v8 theme colors in vnext theme, we could
  // call convertTheme here to get the correct theme.
  public getThemeForMode(theme: AppTheme): ITheme {
    return this.AppThemeListV8[theme];
  }

  // Eventually we will create an actual method that converts the vnext theme to one for
  // v8, but until we have full themes from design, we will use this for now
  // we will be plugging the legacy v8 theme colors into our vnexttheme
  // so then we can create a theme here by pulling those colors
  public convertVNextThemeToV8Theme(_theme?: CustomAppTheme): ITheme {
    if (this.currentTheme === AppTheme.Light) {
      return this.AppThemeListV8[AppTheme.Light];
    }
    return this.AppThemeListV8[AppTheme.Dark];
  }

  public registerOnThemeChanged(callback: () => void) {
    if (this.themeChangedCallbacks.indexOf(callback) === -1) {
      this.themeChangedCallbacks.push(callback);
    }
  }
  public unregisterOnThemeChanged(callback: () => void) {
    const i = this.themeChangedCallbacks.indexOf(callback);
    if (i === -1) {
      return;
    }
    this.themeChangedCallbacks.splice(i, 1);
  }
}
